import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Shield, AlertCircle, CreditCard } from "lucide-react";
import SEOHead, { generateWebPageSchema } from "@/components/SEOHead";

export default function PlatformFees() {
  const [itemValue, setItemValue] = useState(500);
  const [txType, setTxType] = useState('borrow');

  const platformFee = (itemValue * 0.07).toFixed(2);
  const borrowerServiceFee = Math.max(itemValue * 0.07, 2).toFixed(2);
  const lenderReceives = (itemValue - parseFloat(platformFee)).toFixed(2);

  const seoData = {
    title: "Platform Fees | Paradise Borrowed",
    description: "Simple, transparent pricing for everyone. Lenders keep 93%, borrowers pay a small service fee. Fee calculator and full breakdown. No hidden costs, no subscriptions.",
    url: "https://www.paradiseborrowed.com/fees"
  };

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        canonicalUrl={seoData.url}
        type="website"
        schema={generateWebPageSchema(seoData)}
      />
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="fees-page">
      
      {/* Hero */}
      <div className="text-center pb-12 mb-12 border-b border-stone-200">
        <p className="text-xs tracking-widest uppercase text-stone-400 mb-4">Transparent pricing</p>
        <h1 className="text-4xl sm:text-5xl text-stone-900 mb-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
          Simple fees.<br/>
          <span className="text-coral" style={{ fontFamily: "'DM Serif Display', serif", fontStyle: 'italic' }}>No surprises.</span>
        </h1>
        <p className="text-stone-500 max-w-lg mx-auto leading-relaxed">
          Lenders pay 7% on their payouts. Borrowers pay a small service fee at checkout. Every fee is shown clearly before you confirm.
        </p>
      </div>

      {/* Promotional Offer */}
      <p className="text-xs tracking-widest uppercase text-stone-400 mb-4">Promotional offer</p>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-12" data-testid="promo-section">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-stone-900">First 10 Lenders Per Location</p>
            <p className="text-sm text-stone-600 mt-1 leading-relaxed">
              The first 10 users in each city (US) or island (Caribbean) who publish a listing receive a <strong>lifetime lender-fee waiver on their first 10 listings</strong>. 
              Starting with your 11th listing, the standard 7% lender fee applies.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-4 border border-amber-200/50">
            <p className="text-xs text-stone-500 mb-1">Listings 1-10 (eligible users)</p>
            <p className="text-xl font-medium text-amber-700">0% lender fee</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-amber-200/50">
            <p className="text-xs text-stone-500 mb-1">Listings 11+ (eligible users)</p>
            <p className="text-xl font-medium text-stone-900">7% lender fee</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-amber-200/50">
            <p className="text-xs text-stone-500 mb-1">All listings (non-eligible)</p>
            <p className="text-xl font-medium text-stone-900">7% lender fee</p>
          </div>
        </div>
        <p className="text-xs text-stone-500 mt-4 leading-relaxed">
          Eligibility is determined by the order in which users publish their first listing in a given location. 
          For example, "Miami, FL" is tracked separately from "Fort Lauderdale, FL" and "Grand Cayman, Cayman Islands" 
          is separate from "Nassau, Bahamas". Slots are claimed at the time of first listing publication, not at sign-up. 
          If you are not among the first 10 for your location, the standard 7% fee applies to all listings.
        </p>
      </div>

      {/* Platform Fee Cards */}
      <p className="text-xs tracking-widest uppercase text-stone-400 mb-4">Platform fee</p>
      <div className="grid md:grid-cols-3 gap-4 mb-12">
        {/* Main fee card */}
        <div className="bg-white border-2 border-coral rounded-2xl p-6">
          <span className="inline-block text-xs bg-coral-light text-orange-700 px-3 py-1 rounded-md mb-4">All transactions</span>
          <div className="font-heading text-5xl text-stone-900 mb-1">
            7<sup className="text-xl text-stone-400 font-sans">% all around</sup>
          </div>
          <p className="font-medium text-stone-900 mt-3 mb-2">Simple, fair pricing</p>
          <p className="text-sm text-stone-500 leading-relaxed">
            We take a 7% fee from each side of every transaction — 7% from the lender's payout, and a 7% service fee from the borrower at checkout (minimum $2). The full breakdown is shown to both sides before any payment is confirmed.
          </p>
        </div>

        {/* What it covers */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <span className="inline-block text-xs bg-stone-100 text-stone-600 px-3 py-1 rounded-md mb-4">What it covers</span>
          <p className="font-medium text-stone-900 mt-2 mb-3">Included in every transaction</p>
          <ul className="text-sm text-stone-500 space-y-2">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-coral mt-0.5 shrink-0" /> Payment processing via Stripe</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-coral mt-0.5 shrink-0" /> Trust & Safety team operations</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-coral mt-0.5 shrink-0" /> Dispute mediation services</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-coral mt-0.5 shrink-0" /> Platform support & infrastructure</li>
          </ul>
        </div>

        {/* No hidden costs */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <span className="inline-block text-xs bg-stone-100 text-stone-600 px-3 py-1 rounded-md mb-4">No hidden costs</span>
          <p className="font-medium text-stone-900 mt-2 mb-3">What we never charge</p>
          <ul className="text-sm text-stone-500 space-y-2">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-coral mt-0.5 shrink-0" /> No listing fees</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-coral mt-0.5 shrink-0" /> No subscription required</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-coral mt-0.5 shrink-0" /> No charge for declined bookings</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-coral mt-0.5 shrink-0" /> Cancellations per lender's policy</li>
          </ul>
        </div>
      </div>

      {/* Fee Calculator */}
      <p className="text-xs tracking-widest uppercase text-stone-400 mb-4">Fee calculator</p>
      <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-12">
        <p className="font-medium text-stone-900 mb-6">Estimate your costs</p>
        
        {/* Item value slider */}
        <div className="flex items-center gap-4 mb-4">
          <label className="text-sm text-stone-500 w-24">Item value</label>
          <input 
            type="range" 
            min="10" 
            max="5000" 
            step="10" 
            value={itemValue}
            onChange={(e) => setItemValue(parseInt(e.target.value))}
            className="flex-1 h-1 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-coral"
          />
          <span className="text-sm font-medium text-stone-900 w-20 text-right">${itemValue.toLocaleString()}</span>
        </div>

        {/* Transaction type */}
        <div className="flex items-center gap-4 mb-6">
          <label className="text-sm text-stone-500 w-24">Transaction</label>
          <div className="flex gap-2">
            <button 
              onClick={() => setTxType('borrow')}
              className={`text-xs px-4 py-2 rounded-md border transition-colors ${txType === 'borrow' ? 'bg-coral text-white border-coral' : 'bg-white text-stone-600 border-stone-300 hover:border-coral'}`}
            >
              Borrowing
            </button>
            <button 
              onClick={() => setTxType('buy')}
              className={`text-xs px-4 py-2 rounded-md border transition-colors ${txType === 'buy' ? 'bg-coral text-white border-coral' : 'bg-white text-stone-600 border-stone-300 hover:border-coral'}`}
            >
              Purchase
            </button>
          </div>
        </div>

        <hr className="border-stone-200 mb-6" />

        {/* Results */}
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="bg-stone-100 rounded-xl p-4" data-testid="calc-lender-fee">
            <p className="text-xs text-stone-500 mb-1">Lender fee (7%)</p>
            <p className="text-xl font-medium text-stone-900">${platformFee}</p>
          </div>
          <div className="bg-stone-100 rounded-xl p-4" data-testid="calc-borrower-fee">
            <p className="text-xs text-stone-500 mb-1">Borrower service fee (7%, min $2)</p>
            <p className="text-xl font-medium text-stone-900">${borrowerServiceFee}</p>
          </div>
          <div className="bg-stone-100 rounded-xl p-4" data-testid="calc-lender-receives">
            <p className="text-xs text-stone-500 mb-1">Lender receives</p>
            <p className="text-xl font-medium text-coral">${lenderReceives}</p>
          </div>
        </div>

        <p className="text-xs text-stone-400 mt-4 leading-relaxed">
          The lender fee is deducted from the lender's payout. The borrower service fee is added on top of the listed price and shown at checkout.
        </p>
      </div>

      {/* Dispute Resolution */}
      <p className="text-xs tracking-widest uppercase text-stone-400 mb-4">Dispute resolution</p>
      <div className="border border-stone-200 rounded-2xl overflow-hidden mb-12">
        <div className="bg-coral-light px-6 py-4 border-b border-orange-200">
          <p className="font-medium text-orange-800">What happens when a return is disputed</p>
          <p className="text-sm text-orange-700">Paradise Borrowed's Trust & Safety team reviews all claims and issues a written determination — typically within 5 business days.</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 p-6">
          {/* Borrower protections */}
          <div>
            <p className="text-xs tracking-wider uppercase text-stone-400 mb-4">Borrower protections</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-coral mt-2 shrink-0"></div>
                <p className="text-sm text-stone-600">Borrower may submit photos, in-app messages, and transaction timestamps as evidence</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-coral mt-2 shrink-0"></div>
                <p className="text-sm text-stone-600">Responding party has 48 hours to submit evidence — failure to respond may result in a default finding against them</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-coral mt-2 shrink-0"></div>
                <p className="text-sm text-stone-600">If the lender's claim is found to be false or fabricated, the lender is permanently banned</p>
              </div>
            </div>
          </div>

          {/* Lender protections */}
          <div>
            <p className="text-xs tracking-wider uppercase text-stone-400 mb-4">Lender protections</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-coral mt-2 shrink-0"></div>
                <p className="text-sm text-stone-600">Lender has 48 hours after return confirmation to inspect the item and file a dispute</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-coral mt-2 shrink-0"></div>
                <p className="text-sm text-stone-600">Pre- and post-booking photographs and in-app messages are accepted as evidence</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-coral mt-2 shrink-0"></div>
                <p className="text-sm text-stone-600">If verifiable damage is confirmed, Paradise Borrowed will work with both parties to resolve the cost fairly</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-coral mt-2 shrink-0"></div>
                <p className="text-sm text-stone-600">If the borrower is found to have provided false information, they are permanently banned from the platform</p>
              </div>
            </div>
          </div>
        </div>

        {/* Escalation bar */}
        <div className="mx-6 mb-6 p-4 bg-stone-100 rounded-xl border-l-4 border-coral">
          <p className="font-medium text-sm text-stone-900 mb-1">Unresolved after internal mediation?</p>
          <p className="text-xs text-stone-600 leading-relaxed">
            If either party remains unsatisfied with our determination, disputes may be escalated to binding arbitration administered by the AAA or JAMS under their Consumer Arbitration Rules. A small claims court carve-out applies. All disputes are governed by the laws of the State of Delaware. See our full <Link to="/trust" className="text-coral hover:underline">Dispute Resolution Policy</Link> for complete terms.
          </p>
        </div>
      </div>

      {/* Trust Strip */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6 bg-white border border-stone-200 rounded-2xl mb-12">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
            <Check className="w-4 h-4 text-coral" />
          </div>
          <div>
            <p className="font-medium text-sm text-stone-900">Full fee transparency</p>
            <p className="text-xs text-stone-500">Every fee shown before you confirm. No surprises at checkout.</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
            <Shield className="w-4 h-4 text-coral" />
          </div>
          <div>
            <p className="font-medium text-sm text-stone-900">Secure payments</p>
            <p className="text-xs text-stone-500">All payments processed by Stripe — industry-leading security.</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
            <AlertCircle className="w-4 h-4 text-coral" />
          </div>
          <div>
            <p className="font-medium text-sm text-stone-900">Zero-tolerance fraud policy</p>
            <p className="text-xs text-stone-500">False claims by either party result in permanent removal.</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
            <CreditCard className="w-4 h-4 text-coral" />
          </div>
          <div>
            <p className="font-medium text-sm text-stone-900">Lender insurance recommended</p>
            <p className="text-xs text-stone-500">We recommend lenders maintain appropriate insurance for their items.</p>
          </div>
        </div>
      </div>

      {/* Legal Note */}
      <div className="border-t border-stone-200 pt-6">
        <p className="text-xs text-stone-400 leading-relaxed">
          Platform fees are subject to change; current rates are displayed at checkout. Fee allocation between lender and borrower is disclosed at the time of booking confirmation, as set forth in our <Link to="/terms-of-service" className="text-coral hover:underline">Terms of Service</Link>. Dispute resolution is governed exclusively by the Paradise Borrowed <Link to="/trust" className="text-coral hover:underline">Dispute Resolution Policy</Link>, incorporated by reference into our Terms of Service. Unresolved disputes are subject to binding arbitration under AAA or JAMS Consumer Arbitration Rules in Wilmington, Delaware. For questions: <a href="mailto:info@paradiseborrowed.com" className="text-coral hover:underline">info@paradiseborrowed.com</a>. See also our <Link to="/privacy-policy" className="text-coral hover:underline">Privacy Policy</Link>.
        </p>
      </div>
      </div>
    </>
  );
}
